import type { SecretStore } from "@foreman/crypto";

/** Async object storage that can hold structured-cloneable values, including CryptoKeys (IndexedDB-shaped). */
export interface ObjectStore {
  get(key: string): Promise<unknown | undefined>;
  set(key: string, value: unknown): Promise<void>;
  delete(key: string): Promise<void>;
}

interface Sealed {
  iv: Uint8Array;
  data: Uint8Array;
}

const WRAPPING_KEY = "wrapping-key";

/**
 * Secrets for a browser/PWA: each secret is AES-GCM encrypted with a *non-extractable* key that is
 * itself kept in IndexedDB. Reading the database (disk copy, backup, a stolen profile) yields only
 * ciphertext and a key whose bytes cannot be exported. Honest limits: script running on our own
 * origin (XSS) can still ask the browser to decrypt, and the secret is in JS memory while in use.
 */
export class WebCryptoSecretStore implements SecretStore {
  readonly description = "this browser (encrypted with a non-extractable key)";

  constructor(
    private readonly objects: ObjectStore,
    private readonly subtle: SubtleCrypto = globalThis.crypto.subtle,
  ) {}

  private async key(): Promise<CryptoKey> {
    const existing = await this.objects.get(WRAPPING_KEY);
    if (existing && typeof existing === "object" && "type" in existing) return existing as CryptoKey;
    const key = await this.subtle.generateKey({ name: "AES-GCM", length: 256 }, false, ["encrypt", "decrypt"]);
    await this.objects.set(WRAPPING_KEY, key);
    return key;
  }

  async get(name: string): Promise<Uint8Array | null> {
    const sealed = (await this.objects.get(`secret:${name}`)) as Sealed | undefined;
    if (!sealed) return null;
    const plain = await this.subtle.decrypt({ name: "AES-GCM", iv: new Uint8Array(sealed.iv) }, await this.key(), new Uint8Array(sealed.data));
    return new Uint8Array(plain);
  }

  async set(name: string, value: Uint8Array): Promise<void> {
    const iv = globalThis.crypto.getRandomValues(new Uint8Array(12));
    const data = new Uint8Array(await this.subtle.encrypt({ name: "AES-GCM", iv }, await this.key(), new Uint8Array(value)));
    await this.objects.set(`secret:${name}`, { iv, data } satisfies Sealed);
  }

  async delete(name: string): Promise<boolean> {
    const had = (await this.objects.get(`secret:${name}`)) !== undefined;
    await this.objects.delete(`secret:${name}`);
    return had;
  }
}

export class MemoryObjectStore implements ObjectStore {
  readonly map = new Map<string, unknown>();
  async get(key: string) {
    return this.map.get(key);
  }
  async set(key: string, value: unknown) {
    this.map.set(key, value);
  }
  async delete(key: string) {
    this.map.delete(key);
  }
}
